import { List } from 'immutable'
import { loadingAsyncState, unloadedAsyncState } from 'widgets-for-react'
import {
  ctaSrc,
  ctaSrcSet,
  featuredContentSrc,
  featuredContentSrcSet,
  linkGridSrc,
  linkGridSrcSet,
  logoWallSrc,
  logoWallSrcSet,
  mediaNestedSrc,
  mediaNestedSrcSet,
  mediaSrc,
  mediaSrcSet,
  quoteSrc,
  quoteSrcSet,
  textImageSrc,
  textImageSrcSet,
  advertisementSrc,
  advertisementSrcSet
} from '../../../../utils/imageSizes'
import { getImageUrls } from '../../../../utils/sharepointImageUrls'
import { parseVideo } from '../../../../utils/video'
import { allCasesBlockApi } from '../../blocks/allCasesBlock/allCasesBlockApi'
import { allNewsBlockApi } from '../../blocks/allNewsBlock/allNewsBlockApi'
import { allSubscriptionsBlockApi } from '../../blocks/allSubscriptionsBlock/allSubscriptionsBlockApi'
import { Block } from '../../blocks/blocksState'
import { childCourseGroupsBlockApi } from '../../blocks/childCourseGroupsBlock/childCourseGroupsBlockApi'
import { childDossiersBlockApi } from '../../blocks/childDossiersBlock/childDossiersBlockApi'
import { dossierArticlesBlockApi } from '../../blocks/dossierArticlesBlock/dossierArticlesBlockApi'
import { allEventsBlockApi } from '../../blocks/eventOverviewBlock/allEventsBlockApi'
import { initialExpandBlockState } from '../../blocks/expandBlock/expandBlockState'
import { NestedBlock } from '../../blocks/expandBlock/expandBlockWidget'
import { relatedContentBlockApi } from '../../blocks/relatedContentBlock/relatedContentBlockApi'
import { RelatedContentFilter } from '../../blocks/relatedContentBlock/relatedContentBlockWidget'
import { CoursePage, Page } from '../../cmsPageState'
import {
  ActualityOverviewBlock,
  CaseOverviewBlock,
  ChildDossiersBlock,
  ContactPersonBlockData,
  CourseDetailsBlock,
  CourseOverviewBlock,
  CourseSearchBarBlock,
  KnowledgeSearchBarBlock,
  CtaBlock,
  CtaWithImageBlock,
  DossierArticlesBlock,
  DownloadBlock,
  EventDetailsBlock,
  EventOverviewBlock,
  ExpandableContentBlocks,
  FeaturedContentBlock,
  GridBlocksImageBlock,
  GridBlocksPlainBlock,
  IframeBlock,
  LinksListBlock,
  MediaBlock,
  MembershipBlock,
  QuoteBlock,
  QuoteWithImageBlock,
  RelatedContentBlock,
  SharepointBlockData,
  TextBlock,
  TextWithImageBlock,
  ActualitySearchBarBlock,
  LogoSliderBlock,
  GridBlocksNewBlock,
  AdvertisementBlock
} from '../types/blockData'
import { relatedActualityActualitiesBlockApi, relatedDossierActualitiesBlockApi } from '../../blocks/relatedActualitiesBlock/relatedActualitiesBlockApi'
import { contactPersonBlockApi } from '../../blocks/contactPersonBlock/contactPersonBlockApi'
import { allCourseCategoriesBlockApi } from '../../blocks/allCourseCategoriesBlock/allCourseCategoriesBlockApi'
import { initialCourseDetailsState } from '../../blocks/courseDetailsBlock/courseDetailsBlockState'
import { GetCoursesByGroupId, getData } from '../../../../utils/api'
import { initialEventDetailsState } from '../../blocks/eventDetailsBlock/eventDetailsBlock.state'

export const transformSharepointBlockData = <P extends Page>(
  block: SharepointBlockData,
  index: number,
  page: P
): Block | null => {
  switch (block.kind) {
    case 'featured_content':
      return featuredContentBlock(block, index)
    case 'text_with_image':
      return textImageBlock(block, index)
    case 'text':
      return textBlock(block, index)
    case 'cta':
      return ctaBlock(block, index)
    case 'cta_with_image':
      return ctaImageBlock(block, index)
    case 'links_list':
      return linkListBlock(block, index)
    case 'downloads':
      return downloadBlock(block, index)
    case 'quote':
      return quoteBlock(block, index)
    case 'quote_with_image':
      return quoteBlockImage(block, index)
    case 'media':
      return mediaBlock(block, index)
    case 'expandable_content_blocks':
      return expandBlock(block, index)
    case 'grid_blocks_plain':
      return linkGridBlockPlain(block, index)
    case 'grid_blocks_image':
      return linkGridBlockImage(block, index)
    case 'relatedContent':
      return relatedContentBlock(block, index)
    case 'page-properties':
      return null
    case 'child_dossiers':
      return childDossiersBlock(block, index, page)
    case 'dossier_articles':
      return dossierArticlesBlock(block, index, page)
    case 'course-overview':
      return courseOverviewBlock(block, index, page)
    case 'case-overview':
      return caseOverviewBlock(block, index, page)
    case 'membership':
      return membershipBlock(block, index, page)
    case 'actuality-overview':
      return actualityOverviewBlock(block, index, page)
    case 'course-details':
      return courseDetailsBlock(index, page as CoursePage)
    case 'event-details':
      return eventDetailsBlock(index, page)
    case 'event-overview':
      return eventOverviewBlock(block, index, page)
    case 'incompany':
      return incompanyBlock()
    case 'related-actualities':
      return relatedActualitiesBlock(index, page)
    case 'contact_person':
      return contactPersonBlock(index, block)
    case 'iframe':
      return iframeBlock(index, block)
    case 'course-search-bar':
      return courseSearchBarBlock(index, block)
    case 'knowledge-search-bar':
      return knowledgeSearchBarBlock(index,block)
    case 'actuality-search-bar':
      return actualitySearchBarBlock(index,block)
    case 'logo-slider':
      return logoSliderBlock(index,block)
    case 'grid_blocks_new':
      return linkGridBlockNew(block, index)
    case 'advertisement':
      return advertisementBlock(index, block)
  }
}

export const rerouteSPUrls = (text: string): string =>
  !!text
    ? text.replace(/http[\S]+\/sites\/[^/]+/g, '/api/v1/sharepoint/file')
    : text

const featuredContentBlock = (
  block: FeaturedContentBlock,
  index: number
): Block => ({
  kind: 'featuredContentBlock',
  index,
  imageLocation: block.image_position,
  image: {
    alt: block.image.alt,
    src: featuredContentSrc,
    srcset: featuredContentSrcSet,
    sizes: 'auto',
    lazyload: true,
    width: '320',
    height: '216',
    urls: getImageUrls(rerouteSPUrls(block.image.url)),
    keepOriginalSize: block.image.keepOriginalSize
  },
  label: rerouteSPUrls(block.label),
  title: rerouteSPUrls(block.title),
  intro: rerouteSPUrls(block.introduction),
  primaryAction: {
    title: block.link.title,
    url: rerouteSPUrls(block.link.url)
    // SVGIcon: {
    //   width: '20px',
    //   height: '20px',
    // },
  },
  inview: {
    threshold: 0.1,
    triggerOnce: true
  },
  meta: block.meta
})

const textImageBlock = (block: TextWithImageBlock, index: number): Block => {
  return {
    kind: 'textImageBlock',
    index,
    image: {
      alt: block.image.alt,
      src: textImageSrc,
      srcset: textImageSrcSet,
      sizes: 'auto',
      lazyload: true,
      width: '320',
      height: '216',
      urls: getImageUrls(rerouteSPUrls(block.image.url)),
      keepOriginalSize: block.image.keepOriginalSize
    },
    imageLocation: block.image_position,
    label: rerouteSPUrls(block.label),
    title: rerouteSPUrls(block.title),
    text: rerouteSPUrls(block.text),
    meta: rerouteSPUrls(block.meta),
    primaryAction: !block.link
      ? undefined
      : {
          url: rerouteSPUrls(block.link.url),
          title: block.link.title
          // SVGIcon: {
          //   width: '20px',
          //   height: '20px',
          // },
        },
    secondaryAction: !block.button
      ? undefined
      : {
          url: rerouteSPUrls(block.button.url),
          title: block.button.title
          // SVGIcon: {
          //   width: '20px',
          //   height: '20px',
          // },
        },
    inview: {
      threshold: 0.1,
      triggerOnce: true
    }
  }
}

const textBlock = (block: TextBlock, index: number): Block => ({
  kind: 'textBlock',
  index,
  title: block.title ? block.title : '',
  intro: block.intro,
  text: rerouteSPUrls(block.text),
  secondaryAction:
    block.button && block.button.title && block.button.url
      ? {
          url: rerouteSPUrls(block.button.url),
          title: block.button.title
          // SVGIcon: {
          //   width: '20px',
          //   height: '20px',
          // },
        }
      : undefined,
  primaryAction:
    block.link && block.link.title && block.link.url
      ? {
          url: rerouteSPUrls(block.link.url),
          title: block.link.title
          // SVGIcon: {
          //   width: '20px',
          //   height: '20px',
          // },
        }
      : undefined,
  isLinkType: block.linkType ? block.linkType : false,
  inview: {
    threshold: 0.1,
    triggerOnce: true
  }
})

const isSmall = (type: string | undefined): boolean => {
  if(type != undefined && type == 'small') { return true; }

  return false;
}

const ctaBlock = (block: CtaBlock, index: number): Block => ({
  kind: 'ctaBlock',
  index,
  title: isSmall(block.type) ? '' : block.title ? block.title : '',
  intro: block.intro ? rerouteSPUrls(block.intro) : '',
  type: block.type ? block.type : 'big',
  phone: block.phone,
  buttons: block.buttons ? block.buttons : 'none',
  primaryAction:
    block.primary_button == undefined
      ? undefined
      : {
          url: rerouteSPUrls(block.primary_button.url),
          title: block.primary_button.title
        },
  secondaryAction: isSmall(block.type) ? undefined :
    block.secondary_button == undefined
      ? undefined
      : {
          url: rerouteSPUrls(block.secondary_button.url),
          title: block.secondary_button.title
        },
  inview: {
    threshold: 0.1,
    triggerOnce: true
  }
})

const ctaImageBlock = (block: CtaWithImageBlock, index: number): Block => ({
  kind: 'ctaBlock',
  index,
  title: block.title ? block.title : '',
  intro: block.intro ? rerouteSPUrls(block.intro) : '',
  type: block.type ? block.type : 'big',
  phone: block.phone,
  image: block.image
    ? {
        alt: block.image.alt,
        src: ctaSrc,
        srcset: ctaSrcSet,
        sizes: 'auto',
        lazyload: true,
        width: '160',
        height: '160',
        urls: getImageUrls(rerouteSPUrls(block.image.url)),
        keepOriginalSize: block.image.keepOriginalSize
      }
    : undefined,
  primaryAction:
    block.primary_button == undefined
      ? undefined
      : {
          url: rerouteSPUrls(block.primary_button.url),
          title: block.primary_button.title
          // SVGIcon: {
          //   width: '20px',
          //   height: '20px',
          // },
        },
  secondaryAction:
    block.secondary_button == undefined
      ? undefined
      : {
          url: rerouteSPUrls(block.secondary_button.url),
          title: block.secondary_button.title
          // SVGIcon: {
          //   width: '20px',
          //   height: '20px',
          // },
        },
  inview: {
    threshold: 0.1,
    triggerOnce: true
  }
})

const quoteBlock = (block: QuoteBlock, index: number): Block => ({
  kind: 'quoteBlock',
  index,
  text: block.text ? rerouteSPUrls(block.text) : '',
  author: block.author ? block.author : '',
  primaryAction:
    block.link !== undefined && block.link.url.trim().length > 0
      ? {
          url: rerouteSPUrls(block.link.url),
          title: block.link.title
          // SVGIcon: {
          //   width: '20px',
          //   height: '20px',
          // },
        }
      : undefined,
  inview: {
    threshold: 0.1,
    triggerOnce: true
  },
  company: block.company ? block.company : ''
})

const quoteBlockImage = (block: QuoteWithImageBlock, index: number): Block => ({
  kind: 'quoteBlock',
  index,
  text: block.kind === 'quote_with_image' ? rerouteSPUrls(block.text) : '',
  author: block.author ? block.author : '',
  primaryAction:
    block.link !== undefined && block.link.url.trim().length > 0
      ? {
          url: rerouteSPUrls(block.link.url),
          title: block.link.title
          // SVGIcon: {
          //   width: '20px',
          //   height: '20px',
          // },
        }
      : undefined,
  image: {
    alt: block.image.alt,
    src: quoteSrc,
    srcset: quoteSrcSet,
    sizes: 'auto',
    lazyload: true,
    width: '160',
    height: '160',
    urls: getImageUrls(rerouteSPUrls(block.image.url)),
    keepOriginalSize: block.image.keepOriginalSize
  },
  inview: {
    threshold: 0.1,
    triggerOnce: true
  },
  company: block.company ? block.company : ''
})

export const expandBlock = (
  block: ExpandableContentBlocks,
  index: number
): Block => ({
  kind: 'expandBlock',
  index,
  title: block.title,
  intro: rerouteSPUrls(block.intro),
  items: block.items.map((item, i) => ({
    isOpen: false,
    index: index,
    title: item.title,
    subtitle: rerouteSPUrls(item.subtitle),
    text: rerouteSPUrls(item.text),
    open: false,
    useSVGIcons: true,
    nestedBlocks: List(item.blocks)
      .map<NestedBlock>((b, i) =>
        b.kind === 'text'
          ? {
              kind: 'textBlockNested',
              index: i,
              intro: b.intro ? rerouteSPUrls(b.intro) : '',
              text: b.text ? rerouteSPUrls(b.text) : '',
              inview: {
                threshold: 0.1,
                triggerOnce: true
              }
            }
          : b.kind === 'media'
          ? {
              kind: 'mediaBlockNested',
              index: i,
              items: b.items.map(item => ({
                kind: item.type && item.type === 'image' ? 'image' : 'video',
                // display: item.displayOption ? item.displayOption : 'content-width',
                display: 'content-width',
                author: item.author ? item.author : '',
                caption: item.caption ? item.caption : '',
                image: {
                  alt: item.image.alt,
                  src: mediaNestedSrc,
                  srcset: mediaNestedSrcSet,
                  sizes: 'auto',
                  lazyload: true,
                  width: '570',
                  urls: getImageUrls(rerouteSPUrls(item.image.url)),
                  keepOriginalSize: item.image.keepOriginalSize
                },
                video: parseVideo(rerouteSPUrls(item.videoUrl))
              })),
              inview: {
                threshold: 0.1,
                triggerOnce: true
              }
            }
          : b.kind === 'quote'
          ? {
              kind: 'quoteBlockNested',
              index,
              text: rerouteSPUrls(b.text),
              author: b.author,
              inview: {
                threshold: 0.1,
                triggerOnce: true
              }
            }
          : {
              kind: 'linkBlockNested',
              index: i,
              items: b.linkList.map(link => ({
                url: rerouteSPUrls(link.link.url),
                title: link.link.title,
                text: rerouteSPUrls(link.description),
                label: rerouteSPUrls(link.label),
                itemTitle: link.title
              })),
              SVGIcon: {
                width: '18px',
                height: '18px'
              },
              inview: {
                threshold: 0.1,
                triggerOnce: true
              }
            }
      )
      .toList()
  })),
  primaryAction: {
    url: rerouteSPUrls(block.link.url),
    title: block.link.title
    // SVGIcon: {
    //   width: '20px',
    //   height: '20px',
    // },
  },
  inview: {
    threshold: 0.1,
    triggerOnce: true
  },
  expandBlockState: initialExpandBlockState(block.items.length)
})

const linkListBlock = (block: LinksListBlock, index: number): Block => ({
  kind: 'linkBlock',
  index,
  title: block.title ? block.title : '',
  intro: block.introduction ? rerouteSPUrls(block.introduction) : '',
  items: block.linkList.map(x => ({
    url: rerouteSPUrls(x.link.url),
    title: x.link.title,
    text: rerouteSPUrls(x.description)
  })),
  SVGIcon: {
    width: '18px',
    height: '18px'
  },
  inview: {
    threshold: 0.1,
    triggerOnce: true
  }
})

const downloadBlock = (block: DownloadBlock, index: number): Block => ({
  kind: 'downloadsBlock',
  index,
  title: block.title ? block.title : '',
  intro: block.introduction ? rerouteSPUrls(block.introduction) : '',
  items: block.items.map(x => ({
    title: x.title,
    text: rerouteSPUrls(x.text),
    fileUrl: rerouteSPUrls(x.fileUrl)
  })),
  SVGIcon: {
    width: '18px',
    height: '18px'
  },
  inview: {
    threshold: 0.1,
    triggerOnce: true
  }
})

const linkGridBlockPlain = (
  block: GridBlocksPlainBlock,
  index: number
): Block => ({
  kind: 'linkGridBlock',
  index,
  withImages: false,
  title: block.title ? block.title : '',
  intro: block.intro ? block.intro : undefined,
  items: block.items.map(e => ({
    ...e,
    title: e && e.title ? e.title : '',
    label: e && rerouteSPUrls(e.label) ? e.label : '',
    intro: e && rerouteSPUrls(e.intro) ? e.intro : '',
    link:
      e && e.link && e.link.title && e.link.url != null ? {
        title: e.link.title,
        url: rerouteSPUrls(e.link.url)
      } : undefined,
    date: e && e.date ? new Date(e.date) : undefined
  })),
  // itemsSVGIcon: {
  //   width: '20px',
  //   height: '20px',
  // },
  primaryAction:
    block.link !== undefined
      ? {
          url: rerouteSPUrls(block.link.url),
          title: block.link.title
          // SVGIcon: {
          //   width: '20px',
          //   height: '20px',
          // },
        }
      : undefined,
  inview: {
    threshold: 0.1,
    triggerOnce: true
  }
})

const childDossiersBlock = (
  block: ChildDossiersBlock,
  index: number,
  page: Page
): Block => ({
  kind: 'childDossiersBlock',
  index,
  title: block.title ? block.title : '',
  inview: {
    threshold: 0.1,
    triggerOnce: true
  },
  childrenState: loadingAsyncState(() =>
    childDossiersBlockApi(
      page.kind === 'dossier' && !!page.sharePointId
        ? page.sharePointId
        : undefined
    )
  )
})

const dossierArticlesBlock = (
  block: DossierArticlesBlock,
  index: number,
  page: Page
): Block => ({
  kind: 'dossierArticlesBlock',
  index,
  title: block.title ? block.title : '',
  inview: {
    threshold: 0.1,
    triggerOnce: true
  },
  articlesState:
    page.kind === 'dossier' && !!page.sharePointId
      ? loadingAsyncState(() => dossierArticlesBlockApi(page.sharePointId))
      : unloadedAsyncState()
})

const courseOverviewBlock = (
  block: CourseOverviewBlock,
  index: number,
  page: Page
): Block => {
  if (page.kind === 'course-group') {
    return {
      kind: 'courseOverviewBlock',
      index,
      title: block.title ? block.title : '',
      inview: {
        threshold: 0.1,
        triggerOnce: true
      },
      SVGIcon: {
        width: '20px',
        height: '22px'
      },
      coursesState: !!page.sharePointId
        ? loadingAsyncState(() => getData(GetCoursesByGroupId(page.sharePointId)))
        : unloadedAsyncState()
    }
  }
  if (page.kind === 'course-category') {
    return {
      kind: 'childCourseGroupsBlock',
      index,
      title: block.title ? block.title : '',
      inview: {
        threshold: 0.1,
        triggerOnce: true
      },
      SVGIcon: {
        width: '20px',
        height: '22px'
      },
      courseGroupsState: !!page.sharePointId
        ? loadingAsyncState(() => childCourseGroupsBlockApi(page.sharePointId))
        : unloadedAsyncState()
    }
  }
  return {
    kind: 'allCourseCategoriesBlock',
    index,
    title: block.title ? block.title : '',
    inview: {
      threshold: 0.1,
      triggerOnce: true
    },
    courseCategoriesState: {
      allCourseCategoriesState: loadingAsyncState(() => allCourseCategoriesBlockApi()),
      query: ''
    }
  }
}

const caseOverviewBlock = (
  block: CaseOverviewBlock,
  index: number,
  page: Page
): Block => {
  return {
    kind: 'allCasesBlock',
    index,
    title: block.title ? block.title : '',
    inview: {
      threshold: 0.1,
      triggerOnce: true
    },

    casesState: loadingAsyncState(() => allCasesBlockApi())
  }
}

const membershipBlock = (
  block: MembershipBlock,
  index: number,
  page: Page
): Block => {
  return {
    kind: 'allSubscriptionsBlock',
    index,
    title: block.title ? block.title : '',
    inview: {
      threshold: 0.1,
      triggerOnce: true
    },
    subscriptionsState: loadingAsyncState(() => allSubscriptionsBlockApi())
  }
}

const actualityOverviewBlock = (
  block: ActualityOverviewBlock,
  index: number,
  page: Page
): Block => {
  const search = new URLSearchParams(document.location.search)
  const currentPageParam = search.get('page')
  const currentPage = currentPageParam ? parseInt(currentPageParam) : 1
  return {
    kind: 'allNewsBlock',
    index,
    title: block.title ? block.title : '',
    inview: {
      threshold: 0.1,
      triggerOnce: true
    },
    SVGIcon: {
      width: '20px',
      height: '22px'
    },
    newsState: loadingAsyncState(() => allNewsBlockApi(currentPage))
  }
}

const eventOverviewBlock = (
  block: EventOverviewBlock,
  index: number,
  page: Page
): Block => {
  return {
    kind: 'allEventsBlock',
    index,
    title: block.title ? block.title : '',
    inview: {
      threshold: 0.1,
      triggerOnce: true
    },
    teaser: block.teaser,
    eventsState: loadingAsyncState(() => allEventsBlockApi(block.teaser))
  }
}

const linkGridBlockImage = (
  block: GridBlocksImageBlock,
  index: number
): Block => ({
  kind: 'linkGridBlock',
  index,
  withImages: true,
  title: block.title,
  intro: rerouteSPUrls(block.intro),
  items: block.items.map(item => ({
    ...item,
    image: item.image
      ? {
          urls: getImageUrls(rerouteSPUrls(item.image.url)),
          alt: item.image.alt ? item.image.alt : '',
          keepOriginalSize: item.image.keepOriginalSize
        }
      : undefined
  })),
  // itemsSVGIcon: {
  //   width: '20px',
  //   height: '20px',
  // },
  imageOptions: {
    src: linkGridSrc,
    srcset: linkGridSrcSet,
    sizes: 'auto',
    lazyload: true,
    width: '320',
    height: '160'
  },
  primaryAction: {
    url: rerouteSPUrls(block.link.url),
    title: block.link.title,
    SVGIcon: {
      width: '20px',
      height: '20px'
    }
  },
  inview: {
    threshold: 0.1,
    triggerOnce: true
  }
})

const linkGridBlockNew = (
  block: GridBlocksNewBlock,
  index: number
): Block => {
  return ({
  kind: 'linkGridBlockNew',
  index,
  withImages: true,
  title: block.title,
  intro: rerouteSPUrls(block.intro),
  items: block.items.map(item => ({
    ...item,
    image: item.image && item.image.url != ''
      ? {
          urls: getImageUrls(rerouteSPUrls(item.image.url)),
          alt: item.image.alt ? item.image.alt : '',
          keepOriginalSize: item.image.keepOriginalSize
        }
      : undefined
  })),
  imageOptions: {
    src: linkGridSrc,
    srcset: linkGridSrcSet,
    sizes: 'auto',
    lazyload: true,
    width: '320',
    height: '160'
  },
  primaryAction: {
    url: rerouteSPUrls(block.link.url),
    title: block.link.title,
    SVGIcon: {
      width: '20px',
      height: '20px'
    }
  },
  inview: {
    threshold: 0.1,
    triggerOnce: true
  }
}) }

const mediaBlock = (block: MediaBlock, index: number): Block => ({
  kind: 'mediaBlock',
  index,
  title: block.title,
  intro: rerouteSPUrls(block.intro),
  items: block.items.map(item => ({
    ...item,
    kind: item.type,
    display: 'content-width',
    caption: item.caption,
    image:
      item.type === 'image'
        ? {
            alt: item.image.alt,
            src: mediaSrc,
            width: '520',
            srcset: mediaSrcSet,
            sizes: 'auto',
            lazyload: true,
            urls: getImageUrls(rerouteSPUrls(item.image.url)),
            keepOriginalSize: item.image.keepOriginalSize
          }
        : undefined,
    video: item.type === 'video' ? parseVideo(rerouteSPUrls(item.videoUrl)) : undefined
  })),
  inview: {
    threshold: 0.1,
    triggerOnce: true
  }
})

const relatedContentBlock = (
  block: RelatedContentBlock,
  index: number
): Block => {
  const filter: RelatedContentFilter =
    block.source === 'department'
      ? {
          source: 'department',
          department: block.department.lookupField
        }
      : block.source === 'specialist'
      ? {
          source: 'specialist',
          specialist: block.specialist.lookupField
        }
      : {
          source: 'tags',
          tags: block.tags.map(tag => tag.lookupField)
        }

  return {
    kind: 'relatedContentBlock',
    index,
    filter: filter,
    relatedContentBlockState: loadingAsyncState(() =>
      relatedContentBlockApi(filter)
    ),
    inview: {
      threshold: 0.1,
      triggerOnce: true
    }
  }
}

const courseDetailsBlock = (
  index: number,
  page: CoursePage
): Block => {
  return {
    kind: 'courseDetailsBlock',
    state: initialCourseDetailsState(page.slug, page, page.Evenement ? page.Evenement.RemoteId : null)
  }
}

const eventDetailsBlock = (
  index: number,
  page: Page
): Block => {
  if (page.kind === 'event') {
    return {
      kind: 'eventDetailsBlock',
      state: initialEventDetailsState(page, page.Bijeenkomst ? page.Bijeenkomst.RemoteId : null )
    }
  }
  return { kind: 'noBlock' }
}

const incompanyBlock = (): Block => ({
  kind: 'incompanyBlock'
})

const relatedActualitiesBlock = (index: number, page: Page): Block => {
  if (page.kind === 'dossier') {
    return {
      kind: 'relatedActualitiesBlock',
      index: index,
      inview: {
        threshold: 0.1,
        triggerOnce: true
      },
      relatedActualitiesState: loadingAsyncState(() =>
        relatedDossierActualitiesBlockApi(page.sharePointId)
      )
    }
  }
  if (page.kind === 'actuality-details') {
    return {
      kind: 'relatedActualitiesBlock',
      index: index,
      inview: {
        threshold: 0.1,
        triggerOnce: true
      },
      relatedActualitiesState: loadingAsyncState(() =>
        relatedActualityActualitiesBlockApi(page.sharePointId)
      )
    }
  }
  return { kind: 'noBlock' }
}

const contactPersonBlock = (
  index: number,
  block: ContactPersonBlockData
): Block => {
  return {
    kind: 'contactPersonBlock',
    index: index,
    inview: {
      threshold: 0.1,
      triggerOnce: true
    },
    contactPersonBlockState: loadingAsyncState(() =>
      contactPersonBlockApi(block.contactPerson.id)
    )
  }
}

const iframeBlock = (
  index: number,
  block: IframeBlock
): Block => {
  return {
    kind: 'iframe',
    index: index,
    inview: {
      threshold: 0.1,
      triggerOnce: true
    },
    url: block.url,
    width: block.width ?? '100',
    widthUnit: block.widthUnit == 'px' ? 'px' : 'percentage',
    height: block.height ?? '100',
    heightUnit: block.heightUnit == 'px' ? 'px' : 'percentage',
  }
}

const courseSearchBarBlock = (
  index: number,
  block: CourseSearchBarBlock
): Block => {
  return {
    kind: 'courseSearchBar',
    index: index,
    inview: {
      threshold: 0.1,
      triggerOnce: true
    },
    courseSearchBarState: {
      query: ""
    }
  }
}

const actualitySearchBarBlock = (
  index: number,
  block: ActualitySearchBarBlock
): Block => {
  return {
    kind: 'actualitySearchBar',
    index: index,
    inview: {
      threshold: 0.1,
      triggerOnce: true
    },
    actualitySearchBarState: {
      query: ""
    }
  }
}

const logoSliderBlock = (
  index: number,
  block: LogoSliderBlock
): Block => {
  return {
    kind: 'logoSlider',
    index: index,
    inview: {
      threshold: 0.1,
      triggerOnce: true
    },
    logoSliderState: {
      items: block.items.map(item => ({
        ...item,
        image: item.image
          ? {
              urls: getImageUrls(rerouteSPUrls(item.image.url)),
              alt: item.image.alt ? item.image.alt : '',
              keepOriginalSize: false,
              width: '160',
              height: '90',
              lazyload: true,
              sizes: '160px',
              srcset: logoWallSrcSet,
              src: logoWallSrc,
            }
          : undefined
      })),
    }
  }
}

const knowledgeSearchBarBlock = (
  index: number,
  block: KnowledgeSearchBarBlock
): Block => {
  return {
    kind: 'knowledgeSearchBar',
    index: index,
    inview: {
      threshold: 0.1,
      triggerOnce: true
    },
    knowledgeSearchBarState: {
      query: ""
    }
  }
}

const advertisementBlock = (
  index: number,
  block: AdvertisementBlock
): Block => {
  return {
    kind: 'advertisementBlock',
    index: index,
    image: {
      alt: block.image.alt,
      src: advertisementSrc,
      srcset: advertisementSrcSet,
      sizes: 'auto', // Todo: check if this attr is correct, and also urls attr
      lazyload: true,
      width: '660',
      height: '95',
      urls: getImageUrls(rerouteSPUrls(block.image.url)),
      keepOriginalSize: block.image.keepOriginalSize
    },
    url: block.link.url,
    inview: {
      threshold: 0.1,
      triggerOnce: true
    },
  }
}
